import { createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { RootState, SettingsState } from "@/types/store";
import { getUsers } from "@/api/settings/thunks";

const ITEMS_PER_PAGE = 10;

const initialState: SettingsState = {
  users: [],
  HOUsers: [],
  AUsers: [],
  SAUsers: [],
  ArchivedUsers: [],
  page: 0,
  HOPage: 0,
  APage: 0,
  SAPage: 0,
  ArchivedPage: 0,
  totalPages: 0,
  HOTotalPages: 0,
  ATotalPages: 0,
  SATotalPages: 0,
  ArchivedTotalPages: 0,
  loading: false,
  HOLoading: false,
  ALoading: false,
  SALoading: false,
  ArchivedLoading: false,
  error: null,
};

export const SettingsSlice = createSlice({
  name: "SettingsSlice",
  initialState,
  reducers: {
    resetSettings: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsers.pending, (state, action) => {
        if (action.meta.arg.type) {
          switch (action.meta.arg.type) {
            case "homeowner":
              state.HOLoading = true;
              break;
            case "admin":
              state.ALoading = true;
              break;
            case "super-admin":
              state.SALoading = true;
              break;
            case "archived":
              state.ArchivedLoading = true;
          }
        } else {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        const currentPage = Number(action.payload.page) || 0;
        const totalPages = Math.ceil(
          (Number(action.payload.total) || 0) / ITEMS_PER_PAGE
        );

        if (action.meta.arg.type) {
          switch (action.meta.arg.type) {
            case "homeowner":
              state.HOUsers = action.payload.users;
              state.HOPage = currentPage;
              state.HOTotalPages = totalPages;
              state.HOLoading = false;
              break;
            case "admin":
              state.AUsers = action.payload.users;
              state.APage = currentPage;
              state.ATotalPages = totalPages;
              state.ALoading = false;
              break;
            case "super-admin":
              state.SAUsers = action.payload.users;
              state.SAPage = currentPage;
              state.SATotalPages = totalPages;
              state.SALoading = false;
              break;
            case "archived":
              state.ArchivedUsers = action.payload.users;
              state.ArchivedPage = currentPage;
              state.ArchivedTotalPages = totalPages;
              state.ArchivedLoading = false;
          }
        } else {
          state.users = action.payload.users;
          state.page = currentPage;
          state.totalPages = totalPages;
          state.loading = false;
        }
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.loading = false;
        state.HOLoading = false;
        state.ALoading = false;
        state.SALoading = false;
        state.error = action.payload as string;
      });
  },
});

export const useSettingsData = () => {
  const {
    users,
    HOUsers,
    AUsers,
    SAUsers,
    ArchivedUsers,
    page,
    HOPage,
    APage,
    SAPage,
    ArchivedPage,
    totalPages,
    HOTotalPages,
    ATotalPages,
    SATotalPages,
    ArchivedTotalPages,
    loading,
    HOLoading,
    ALoading,
    SALoading,
    ArchivedLoading,
    error,
  } = useSelector((state: RootState) => state.Settings);

  return {
    users,
    HOUsers,
    AUsers,
    SAUsers,
    ArchivedUsers,
    page,
    HOPage,
    APage,
    SAPage,
    ArchivedPage,
    totalPages,
    HOTotalPages,
    ATotalPages,
    SATotalPages,
    ArchivedTotalPages,
    loading,
    HOLoading,
    ALoading,
    SALoading,
    ArchivedLoading,
    error,
  };
};

export const { resetSettings } = SettingsSlice.actions;
export default SettingsSlice.reducer;
